import type { CommandHandler, CommandConfig } from './types';

/**
 * Central registry for command handlers
 * Allows registration and lookup of command handlers by alias
 */
class CommandRegistry {
  private handlers: Map<string, CommandHandler> = new Map();
  private configs: Map<string, CommandConfig> = new Map();

  /**
   * Register a command handler
   */
  register(config: CommandConfig): void {
    const { alias, handler } = config;

    if (this.handlers.has(alias)) {
      console.warn(`Command handler for alias "${alias}" is being overwritten`);
    }

    this.handlers.set(alias, handler);
    this.configs.set(alias, config);
  }

  /**
   * Register multiple command handlers at once
   */
  registerBatch(configs: CommandConfig[]): void {
    configs.forEach((config) => this.register(config));
  }

  /**
   * Get a command handler by alias
   */
  get(alias: string): CommandHandler | undefined {
    return this.handlers.get(alias);
  }

  /**
   * Get command configuration by alias
   */
  getConfig(alias: string): CommandConfig | undefined {
    return this.configs.get(alias);
  }

  /**
   * Check if a command handler is registered
   */
  has(alias: string): boolean {
    return this.handlers.has(alias);
  }

  /**
   * Unregister a command handler
   */
  unregister(alias: string): boolean {
    this.configs.delete(alias);
    return this.handlers.delete(alias);
  }

  /**
   * Get all registered command aliases
   */
  getAllAliases(): string[] {
    return Array.from(this.handlers.keys());
  }

  /**
   * Get all registered command configs
   */
  getAllConfigs(): CommandConfig[] {
    return Array.from(this.configs.values());
  }

  /**
   * Clear all registered handlers
   */
  clear(): void {
    this.handlers.clear();
    this.configs.clear();
  }
}

// Export singleton instance
export const commandRegistry = new CommandRegistry();

/**
 * Convenience function to register a command
 */
export function registerCommand(config: CommandConfig): void {
  commandRegistry.register(config);
}

/**
 * Convenience function to register multiple commands
 */
export function registerCommands(configs: CommandConfig[]): void {
  commandRegistry.registerBatch(configs);
}

/**
 * Convenience function to get a command handler
 */
export function getCommandHandler(alias: string): CommandHandler | undefined {
  return commandRegistry.get(alias);
}
